// frontend/src/components/import-export/ResourceImportExport.tsx
// Resource (ingredient) import/export using the generic ImportExportDialog

import { useState } from "react";
import { ImportExportDialog, type ImportExportConfig, type ImportValidationError, type ImportResult } from "./ImportExportDialog";
import { useResources } from "../../hooks/useInventoryData";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "../../services/api";
import type { Resource } from "../../types/models";

// =============================================================================
// Export/Import Data Types
// =============================================================================

interface ResourceExportItem {
  /** ISO date string — set for existing system items so the dialog can show "Créée le …" */
  _createdAt?: string;
  /** Veloce link — used as the primary identity key when present so resources
   *  sharing a display name across Veloce entries don't collide. */
  veloceResourceId?: string | null;
  name: string;
  category: string | null;
  unit: string;
  costPrice: number;
  sellPrice: number;
  currentQuantity: number;
  minQuantity: number | null;
  sku: string | null;
  barcode: string | null;
}

// =============================================================================
// Conversion helpers
// =============================================================================

function resourceToExportItem(resource: Resource): ResourceExportItem {
  return {
    _createdAt: resource.createdAt,
    veloceResourceId: resource.veloceResourceId,
    name: resource.name,
    category: resource.category,
    unit: resource.unit,
    costPrice: Number(resource.costPrice) || 0,
    sellPrice: Number(resource.sellPrice) || 0,
    currentQuantity: Number(resource.currentQuantity) || 0,
    minQuantity: resource.minQuantity,
    sku: resource.sku,
    barcode: resource.barcode,
  };
}

function normalizeImportData(parsed: any): ResourceExportItem[] {
  const items: any[] = Array.isArray(parsed) ? parsed : parsed.data || [];
  return items.map((item) => ({
    veloceResourceId: item.veloceResourceId ?? null,
    name: item.name,
    category: item.category ?? null,
    unit: item.unit ?? "unité",
    costPrice: item.costPrice ?? 0,
    sellPrice: item.sellPrice ?? 0,
    currentQuantity: item.currentQuantity ?? 0,
    minQuantity: item.minQuantity ?? null,
    sku: item.sku ?? null,
    barcode: item.barcode ?? null,
  }));
}

// =============================================================================
// Formatters
// =============================================================================

function formatPrice(v: any): string {
  const n = Number(v);
  if (isNaN(n) || n === 0) return "—";
  return `${n.toFixed(2)} $`;
}

function formatQuantity(v: any): string {
  const n = Number(v);
  if (isNaN(n) || n === 0) return "—";
  return n.toString();
}

// =============================================================================
// ImportExportConfig for resources
// =============================================================================

const resourceConfig: ImportExportConfig<ResourceExportItem> = {
  entityName: "ressource",
  entityNamePlural: "ressources",
  // Identity key: prefer veloceResourceId when present (handles cases where
  // multiple resources share a display name across distinct Veloce entries).
  getKey: (item) =>
    item.veloceResourceId
      ? `vid:${item.veloceResourceId}`
      : `name:${item.name.toLowerCase()}`,
  getDisplayName: (item) => item.name,
  getCategory: (item) => item.category || "",
  comparisonFields: [
    { label: "Nom", key: "name", format: (v) => v || "—" },
    { label: "Catégorie", key: "category", format: (v) => v || "—" },
    { label: "Unité", key: "unit", format: (v) => v || "—" },
    { label: "Coût", key: "costPrice", format: formatPrice },
    { label: "Quantité", key: "currentQuantity", format: formatQuantity },
  ],
  isIdentical: (a, b) => {
    return (
      a.category === b.category &&
      a.unit === b.unit &&
      Math.abs((a.costPrice ?? 0) - (b.costPrice ?? 0)) < 0.01 &&
      Math.abs((a.sellPrice ?? 0) - (b.sellPrice ?? 0)) < 0.01
    );
  },
  // New resources (not in system) are always new; existing ones with same data skip.
  // No special update logic needed for resources.
  getItemDate: (item) => item._createdAt,
};

// =============================================================================
// Validation
// =============================================================================

function validateResourceImport(parsed: any): ImportValidationError[] {
  const errors: ImportValidationError[] = [];

  if (typeof parsed !== "object" || parsed === null) {
    errors.push({ severity: "error", message: "Le fichier ne contient pas un objet JSON valide." });
    return errors;
  }

  const items = Array.isArray(parsed) ? parsed : parsed.data;

  if (!items || !Array.isArray(items)) {
    errors.push({
      severity: "error",
      message: "Structure incompatible : le fichier doit contenir un tableau « data » avec les ressources.",
    });
    return errors;
  }

  if (items.length === 0) {
    errors.push({ severity: "error", message: "Le fichier ne contient aucune ressource." });
    return errors;
  }

  // Detect if this is a menu/recipe file instead of a resource file
  const hasMenuItems = items.some((item: any) => item.menuItem || item.recipe || item.ingredients);
  if (hasMenuItems) {
    errors.push({
      severity: "error",
      message: "Ce fichier contient des articles du menu (recettes), pas des ressources. Utilisez l'onglet « Menu & Recettes » pour l'importer.",
    });
    return errors;
  }

  // Validate item structure
  let missingNames = 0;
  let missingUnits = 0;

  for (const item of items) {
    if (!item.name) missingNames++;
    if (!item.unit) missingUnits++;
  }

  if (missingNames > 0) {
    errors.push({
      severity: "error",
      message: `${missingNames} ressource(s) sans nom détectée(s). Chaque ressource doit avoir un nom.`,
    });
  }

  if (missingUnits > 0 && missingUnits < items.length) {
    errors.push({
      severity: "warning",
      message: `${missingUnits} ressource(s) sans unité. L'unité par défaut « unité » sera utilisée.`,
    });
  }

  return errors;
}

// =============================================================================
// Component
// =============================================================================

interface ResourceImportExportProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ResourceImportExport({ isOpen, onClose }: ResourceImportExportProps) {
  const { data: resources = [] } = useResources();
  const queryClient = useQueryClient();
  const [importing, setImporting] = useState(false);

  const existingItems: ResourceExportItem[] = resources.map(resourceToExportItem);

  const handleExport = (items: ResourceExportItem[]) => {
    const exportData = {
      exportedAt: new Date().toISOString(),
      description: "Resources (ingredients/inventory items)",
      count: items.length,
      data: items,
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ressources_${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImport = async (
    items: { data: ResourceExportItem; resolution?: "incoming" | "existing" }[],
    onProgress?: (current: number, total: number) => void,
  ): Promise<ImportResult> => {
    const toImport = items
      .filter((item) => item.resolution !== "existing")
      .map((item) => item.data);

    if (toImport.length === 0) return { succeeded: 0, failed: [] };

    setImporting(true);
    const result: ImportResult = { succeeded: 0, failed: [] };
    const batchSize = 10;
    const total = toImport.length;

    try {
      for (let i = 0; i < total; i += batchSize) {
        const batch = toImport.slice(i, i + batchSize);
        try {
          await api.post("/data/import/resources", {
            data: batch,
            mode: "upsert",
          });
          result.succeeded += batch.length;
        } catch (err: any) {
          batch.forEach(item => {
            result.failed.push({
              name: item.name,
              error: err?.response?.data?.message || err?.message || "Erreur inconnue",
            });
          });
        }
        onProgress?.(Math.min(i + batchSize, total), total);
      }
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
    } finally {
      setImporting(false);
    }

    return result;
  };

  const handleReset = async (): Promise<boolean> => {
    try {
      await api.post("/data/reset", { confirm: true });
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      return true;
    } catch (err) {
      console.error("Reset failed:", err);
      toast.error("Erreur lors de la réinitialisation.");
      return false;
    }
  };

  return (
    <ImportExportDialog<ResourceExportItem>
      isOpen={isOpen}
      onClose={onClose}
      config={resourceConfig}
      existingItems={existingItems}
      onExport={handleExport}
      onImport={handleImport}
      parseImportData={normalizeImportData}
      validateImportData={validateResourceImport}
      onReset={handleReset}
    />
  );
}
